import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  RefreshCw,
  Search,
  Users
} from 'lucide-react';

const STATUS_CONFIG = {
  draft: { label: 'Draft', bar: 'bg-gray-500', badge: 'bg-gray-100 text-gray-700' },
  scheduled: { label: 'Scheduled', bar: 'bg-blue-600', badge: 'bg-blue-100 text-blue-700' },
  sending: { label: 'Sending', bar: 'bg-emerald-600', badge: 'bg-emerald-100 text-emerald-700' },
  sent: { label: 'Sent', bar: 'bg-teal-600', badge: 'bg-teal-100 text-teal-700' },
  completed: { label: 'Completed', bar: 'bg-green-700', badge: 'bg-green-100 text-green-700' },
  paused: { label: 'Paused', bar: 'bg-amber-500', badge: 'bg-amber-100 text-amber-800' },
  cancelled: { label: 'Cancelled', bar: 'bg-red-500', badge: 'bg-red-100 text-red-700' }
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active campaigns' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'sending', label: 'Sending' },
  { value: 'paused', label: 'Paused' },
  { value: 'draft', label: 'Draft' },
  { value: 'finished', label: 'Sent and completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_CAMPAIGNS_PER_WEEK = 4;
const CAMPAIGN_COLORS = [
  '#2563EB', '#7C3AED', '#059669', '#E11D48', '#0891B2',
  '#EA580C', '#C026D3', '#0F766E', '#4F46E5', '#DB2777',
  '#4D7C0F', '#0369A1', '#B45309', '#9333EA', '#475569'
];

const startOfDay = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const dateKey = date => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0')
].join('-');

const parseScheduleDate = value => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [year, month, day] = text.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  const normalized = text.replace(' ', 'T');
  const hasTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
  const parsed = new Date(hasTimeZone ? normalized : `${normalized}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = date => date.toLocaleDateString(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});

const formatStep = sequenceOrder => (
  Number(sequenceOrder) === 0 ? 'Initial email' : `Follow-up ${Number(sequenceOrder) + 1}`
);

const getStatusConfig = status => STATUS_CONFIG[status] || {
  label: status || 'Unknown',
  bar: 'bg-slate-500',
  badge: 'bg-slate-100 text-slate-700'
};

const matchesStatus = (status, filter) => {
  if (filter === 'all') return true;
  if (filter === 'active') return ['scheduled', 'sending'].includes(status);
  if (filter === 'finished') return ['sent', 'completed'].includes(status);
  return status === filter;
};

const prepareCampaign = campaign => {
  const steps = (campaign.steps || [])
    .map(step => ({ ...step, date: parseScheduleDate(step.scheduled_send_at) }))
    .filter(step => step.date)
    .sort((first, second) => first.date - second.date);
  const campaignStart = parseScheduleDate(campaign.scheduled_at);

  if (campaignStart && !steps.some(step => Number(step.sequence_order) === 0)) {
    steps.unshift({ sequence_order: 0, date: campaignStart, scheduled_send_at: campaign.scheduled_at });
  }

  if (steps.length === 0) {
    return { ...campaign, steps, startDate: null, endDate: null };
  }

  return {
    ...campaign,
    steps,
    startDate: startOfDay(steps[0].date),
    endDate: startOfDay(steps[steps.length - 1].date)
  };
};

const buildCalendarWeeks = month => {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(firstDay);
  start.setDate(firstDay.getDate() - firstDay.getDay());

  return Array.from({ length: 6 }, (_, weekIndex) => (
    Array.from({ length: 7 }, (_, dayIndex) => {
      const date = new Date(start);
      date.setDate(start.getDate() + weekIndex * 7 + dayIndex);
      return date;
    })
  ));
};

const CampaignCalendar = ({ onOpenCampaign }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showFollowUps, setShowFollowUps] = useState(true);

  const loadCalendar = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/email-campaigns/calendar');
      if (!response.data.success) throw new Error('The calendar data is unavailable.');
      setCampaigns(response.data.campaigns || []);
      setTotalCampaigns(Number(response.data.total) || 0);
    } catch (requestError) {
      console.error('Failed to load the campaign calendar:', requestError);
      setError(requestError.response?.data?.error || requestError.message || 'Failed to load the campaign calendar.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const preparedCampaigns = useMemo(
    () => campaigns.map(prepareCampaign),
    [campaigns]
  );
  const campaignColors = useMemo(() => {
    const colorsByCampaign = new Map();
    [...preparedCampaigns]
      .sort((first, second) => Number(first.id) - Number(second.id))
      .forEach((campaign, index) => {
        colorsByCampaign.set(campaign.id, CAMPAIGN_COLORS[index % CAMPAIGN_COLORS.length]);
      });
    return colorsByCampaign;
  }, [preparedCampaigns]);

  const filteredCampaigns = useMemo(() => {
    const searchValue = search.trim().toLowerCase();
    return preparedCampaigns.filter(campaign => (
      matchesStatus(campaign.status, statusFilter)
      && (campaignFilter === 'all' || String(campaign.id) === campaignFilter)
      && (!searchValue || String(campaign.name || '').toLowerCase().includes(searchValue))
    ));
  }, [campaignFilter, preparedCampaigns, search, statusFilter]);

  const scheduledCampaigns = useMemo(
    () => filteredCampaigns.filter(campaign => campaign.startDate),
    [filteredCampaigns]
  );
  const unscheduledCampaigns = useMemo(
    () => filteredCampaigns.filter(campaign => !campaign.startDate),
    [filteredCampaigns]
  );
  const weeks = useMemo(() => buildCalendarWeeks(visibleMonth), [visibleMonth]);
  const todayKey = dateKey(new Date());

  const counts = useMemo(() => ({
    scheduled: preparedCampaigns.filter(campaign => campaign.startDate).length,
    active: preparedCampaigns.filter(campaign => ['scheduled', 'sending'].includes(campaign.status)).length,
    paused: preparedCampaigns.filter(campaign => campaign.status === 'paused').length,
    unscheduled: preparedCampaigns.filter(campaign => !campaign.startDate).length
  }), [preparedCampaigns]);

  const changeMonth = amount => {
    setVisibleMonth(previous => new Date(previous.getFullYear(), previous.getMonth() + amount, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setVisibleMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  if (loading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-lg border bg-white">
        <RefreshCw className="mr-3 animate-spin text-blue-600" size={22} />
        <span className="text-gray-600">Loading campaign calendar...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="font-medium text-red-800">{error}</p>
        <button
          type="button"
          onClick={loadCalendar}
          className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Campaigns Calendar</h2>
          <p className="text-gray-600">View campaign durations and planned email dates.</p>
        </div>
        <button
          type="button"
          onClick={loadCalendar}
          className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { label: 'Scheduled on calendar', value: counts.scheduled, icon: CalendarDays, color: 'text-blue-600 bg-blue-50' },
          { label: 'Active', value: counts.active, icon: RefreshCw, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Paused', value: counts.paused, icon: Clock, color: 'text-amber-600 bg-amber-50' },
          { label: 'Unscheduled', value: counts.unscheduled, icon: Users, color: 'text-gray-600 bg-gray-100' }
        ].map(item => {
          const Icon = item.icon;
          return (
            <div key={item.label} className="flex items-center gap-3 rounded-lg border bg-white p-4">
              <div className={`rounded-lg p-2 ${item.color}`}><Icon size={20} /></div>
              <div>
                <p className="text-2xl font-semibold text-gray-900">{item.value}</p>
                <p className="text-xs text-gray-500">{item.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-lg border bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className="relative min-w-56 flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={17} />
            <input
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search campaigns..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </label>
          <select
            value={campaignFilter}
            onChange={event => setCampaignFilter(event.target.value)}
            className="min-w-52 rounded-lg border border-gray-300 bg-white px-3 py-2"
            aria-label="Filter by campaign"
          >
            <option value="all">All campaigns</option>
            {[...preparedCampaigns]
              .sort((first, second) => String(first.name).localeCompare(String(second.name)))
              .map(campaign => <option key={campaign.id} value={String(campaign.id)}>{campaign.name}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={event => setStatusFilter(event.target.value)}
            className="min-w-44 rounded-lg border border-gray-300 bg-white px-3 py-2"
            aria-label="Filter by campaign status"
          >
            {STATUS_FILTERS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showFollowUps}
              onChange={event => setShowFollowUps(event.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Show follow-up markers
          </label>
        </div>
        {totalCampaigns > campaigns.length && (
          <p className="mt-3 text-xs text-amber-700">
            Showing the first {campaigns.length.toLocaleString()} of {totalCampaigns.toLocaleString()} campaigns.
          </p>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => changeMonth(-1)} className="rounded-md p-2 hover:bg-gray-100" aria-label="Previous month">
              <ChevronLeft size={20} />
            </button>
            <button type="button" onClick={() => changeMonth(1)} className="rounded-md p-2 hover:bg-gray-100" aria-label="Next month">
              <ChevronRight size={20} />
            </button>
            <button type="button" onClick={goToToday} className="rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50">Today</button>
            <h3 className="ml-2 text-lg font-semibold text-gray-900">
              {visibleMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </h3>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <span className="text-xs text-gray-500">Bar colors identify campaigns. Dots identify status:</span>
            {Object.entries(STATUS_CONFIG).map(([status, config]) => (
              <span key={status} className="flex items-center gap-1.5 text-xs text-gray-600">
                <span className={`h-2.5 w-2.5 rounded-full ${config.bar}`} />
                {config.label}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-7 border-b bg-gray-50">
          {DAY_NAMES.map(day => (
            <div key={day} className="border-r px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 last:border-r-0">
              {day}
            </div>
          ))}
        </div>

        <div>
          {weeks.map((week, weekIndex) => {
            const weekStart = startOfDay(week[0]);
            const weekEnd = startOfDay(week[6]);
            const weekCampaigns = scheduledCampaigns
              .filter(campaign => campaign.startDate <= weekEnd && campaign.endDate >= weekStart)
              .sort((first, second) => first.startDate - second.startDate || String(first.name).localeCompare(String(second.name)));
            const visibleWeekCampaigns = weekCampaigns.slice(0, MAX_CAMPAIGNS_PER_WEEK);

            return (
              <div key={dateKey(week[0])} className={`grid grid-cols-7 ${weekIndex < weeks.length - 1 ? 'border-b' : ''}`}>
                {week.map(day => {
                  const dayStart = startOfDay(day);
                  const currentDayKey = dateKey(day);
                  const outsideMonth = day.getMonth() !== visibleMonth.getMonth();
                  const hiddenCount = weekCampaigns.slice(MAX_CAMPAIGNS_PER_WEEK)
                    .filter(campaign => campaign.startDate <= dayStart && campaign.endDate >= dayStart).length;

                  return (
                    <div
                      key={currentDayKey}
                      className={`min-h-[155px] border-r p-1.5 last:border-r-0 ${outsideMonth ? 'bg-gray-50/70' : 'bg-white'}`}
                    >
                      <div className="mb-2 flex justify-end">
                        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-sm ${currentDayKey === todayKey
                          ? 'bg-blue-600 font-semibold text-white'
                          : outsideMonth ? 'text-gray-400' : 'text-gray-700'
                          }`}>
                          {day.getDate()}
                        </span>
                      </div>

                      <div className="space-y-1">
                        {visibleWeekCampaigns.map(campaign => {
                          const isInRange = campaign.startDate <= dayStart && campaign.endDate >= dayStart;
                          if (!isInRange) return <div key={campaign.id} className="h-6" />;

                          const step = campaign.steps.find(item => dateKey(item.date) === currentDayKey);
                          const startsSegment = currentDayKey === dateKey(campaign.startDate) || day.getDay() === 0;
                          const endsSegment = currentDayKey === dateKey(campaign.endDate) || day.getDay() === 6;
                          const status = getStatusConfig(campaign.status);
                          const campaignColor = campaignColors.get(campaign.id);
                          const stepText = step && showFollowUps ? formatStep(step.sequence_order) : '';
                          const label = startsSegment ? campaign.name : stepText;
                          const title = [
                            campaign.name,
                            status.label,
                            `${formatDate(campaign.startDate)} - ${formatDate(campaign.endDate)}`,
                            stepText
                          ].filter(Boolean).join(' | ');

                          return (
                            <button
                              key={campaign.id}
                              type="button"
                              onClick={() => onOpenCampaign(campaign)}
                              title={title}
                              style={{ backgroundColor: campaignColor }}
                              className={`flex h-6 w-full items-center gap-1 overflow-hidden px-1.5 text-left text-[11px] font-medium text-white hover:brightness-110 ${startsSegment ? 'rounded-l-md' : ''} ${endsSegment ? 'rounded-r-md' : ''}`}
                            >
                              <span className={`h-2 w-2 flex-none rounded-full border border-white ${status.bar}`} title={status.label} />
                              {step && showFollowUps && <span className="flex-none text-white">◆</span>}
                              <span className="truncate">{label || '\u00A0'}</span>
                            </button>
                          );
                        })}
                        {hiddenCount > 0 && <p className="px-1 text-[11px] font-medium text-gray-500">+{hiddenCount} more</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {scheduledCampaigns.length === 0 && (
        <div className="rounded-lg border border-dashed bg-white py-10 text-center text-gray-500">
          No scheduled campaigns match these filters.
        </div>
      )}

      {unscheduledCampaigns.length > 0 && (
        <div className="rounded-lg border bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Unscheduled campaigns</h3>
              <p className="text-sm text-gray-500">These campaigns do not have an initial send date.</p>
            </div>
            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">{unscheduledCampaigns.length}</span>
          </div>
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {unscheduledCampaigns.map(campaign => {
              const status = getStatusConfig(campaign.status);
              return (
                <button
                  key={campaign.id}
                  type="button"
                  onClick={() => onOpenCampaign(campaign)}
                  className="flex items-center justify-between gap-3 rounded-lg border p-3 text-left hover:border-blue-300 hover:bg-blue-50"
                >
                  <span className="flex min-w-0 items-center gap-2 truncate text-sm font-medium text-gray-800">
                    <span className="h-3 w-3 flex-none rounded-full" style={{ backgroundColor: campaignColors.get(campaign.id) }} />
                    <span className="truncate">{campaign.name}</span>
                  </span>
                  <span className={`rounded-full px-2 py-1 text-xs font-medium ${status.badge}`}>{status.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignCalendar;
